import { useState } from "react";
import {
  WhmButton,
  WhmCard,
  WhmCardContent,
  WhmDivider,
  WhmStack,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { useNavigate } from "react-router";
import EntityOnboardingSubmitConfirmationModal from "./EntityOnboardingSubmitConfirmationModal";
import {
  KybSectionStatuses,
  OnboardingStatusDto,
} from "../../../data/dtos/onboarding-status.dto";
import EntityOnboardingHeader from "./EntityOnboardingHeader";
import EntityOnboardingSection, {
  EntityOnboardingIcon,
} from "./EntityOnboardingSection";
import EntityOnboardingAdditionalInfoSection from "./EntityOnboardingAdditionalInfoSection";
import translationKeys from "../../../contexts/I18nContext/translationKeys";
import { Translate } from "../../Translation/Translate";
import EntityOnboardingInReviewCard from "./EntityOnboardingInReviewCard";
import { useUnresolvedSectionCounts } from "./useUnresolvedSectionCounts";
import { getSectionData } from "../../../helpers/portfolio";
import { SID } from "@securitize/domain";

export enum ActionButton {
  REVIEW = "REVIEW",
  MANAGE = "MANAGE",
}

const IN_REVIEW_STATUSES: readonly SID.VerificationStatus[] = [
  SID.VerificationStatuses.PROCESSING,
  SID.VerificationStatuses.MANUAL_REVIEW,
];

function navigateToSection(
  navigate: ReturnType<typeof useNavigate>,
  redirectUrl: string,
  useSimpleNavigation: boolean,
) {
  if (useSimpleNavigation) {
    navigate(redirectUrl);
    return;
  }

  const searchParams = new URLSearchParams();
  searchParams.set("disableRedirect", "true");
  searchParams.set("redirectUrl", window.location.pathname);
  navigate(`${redirectUrl}?${searchParams.toString()}`);
}

type EntityOnboardingCardProps = {
  data: OnboardingStatusDto;
  compressed?: boolean;
};

export default function EntityOnboardingCard({
  data,
  compressed = false,
}: EntityOnboardingCardProps) {
  const navigate = useNavigate();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const {
    unresolvedEntityInformationCount,
    unresolvedDocumentsCount,
    unresolvedKeyPartiesCount,
  } = useUnresolvedSectionCounts(data.requestedUpdatesGrouped);

  if (IN_REVIEW_STATUSES.includes(data.verificationStatus)) {
    return <EntityOnboardingInReviewCard data={data} />;
  }

  const kyb = data.kyb;
  const isUpdatesRequiredStatus =
    kyb.entityInformation.status === KybSectionStatuses.UPDATES_REQUIRED ||
    kyb.documents.status === KybSectionStatuses.UPDATES_REQUIRED ||
    kyb.keyParties.status === KybSectionStatuses.UPDATES_REQUIRED;
  const documentsMissingAmount = kyb.documents.missingAmount;
  const editableProfileEnabled = data.editableProfileEnabled;

  const additionalDocumentationCount =
    data.requestedUpdatesGrouped?.additionalDocumentation?.length ?? 0;
  const documentsNeedingUpdatesCount =
    unresolvedDocumentsCount + additionalDocumentationCount;

  const hasKeyPartiesAdditionalInfo = !!(
    data.requestedUpdatesGrouped?.keyPartiesGeneralComment ||
    data.requestedUpdatesGrouped?.legalSignerMissingInfo?.length
  );

  const additionalInfoComment =
    data.requestedUpdatesGrouped?.comments ||
    data.requestedUpdatesGrouped?.additionalComments;

  const entityInformationActionButton = getSectionData(
    kyb.entityInformation.status,
  ).actionButton;
  const documentsActionButton = getSectionData(
    kyb.documents.status,
  ).actionButton;
  const keyPartiesActionButton = getSectionData(
    kyb.keyParties.status,
  ).actionButton;

  return (
    <WhmCard variant="outlined">
      <WhmCardContent
        sx={{
          "&:last-child": {
            paddingBottom: compressed ? "8px" : "24px",
          },
        }}
      >
        <WhmStack>
          <EntityOnboardingHeader
            entityInformationStatus={kyb.entityInformation.status}
            documentsStatus={kyb.documents.status}
            keyPartiesStatus={kyb.keyParties.status}
            requestedUpdatesGrouped={data.requestedUpdatesGrouped}
            setShowSuccessModal={setShowConfirmationModal}
            isUpdatesRequired={isUpdatesRequiredStatus}
            compressed={compressed}
          />
          <EntityOnboardingSection
            icon={EntityOnboardingIcon.entityInformation}
            title={
              <Translate
                translationKey={
                  translationKeys.ONBOARDING_CARD_ENTITY_INFORMATION_TITLE
                }
              />
            }
            subtitle={
              <Translate
                translationKey={
                  translationKeys.ONBOARDING_CARD_ENTITY_INFORMATION_SUBTITLE
                }
              />
            }
            statusMessage={
              kyb.entityInformation.status ===
              KybSectionStatuses.UPDATES_REQUIRED ? (
                <WhmTypography
                  variant="body2"
                  sx={{ textTransform: "uppercase" }}
                >
                  <WhmTypography
                    component="span"
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color:
                        unresolvedEntityInformationCount === 0
                          ? "primary.main"
                          : "error.main",
                    }}
                  >
                    <Translate
                      translationKey={
                        translationKeys.ONBOARDING_CARD_ENTITY_INFORMATION_UPDATES_REQUIRED_COUNT
                      }
                      replacements={{
                        quantity: unresolvedEntityInformationCount,
                      }}
                    />
                  </WhmTypography>{" "}
                  <Translate
                    translationKey={
                      translationKeys.ONBOARDING_CARD_UPDATES_REQUIRED_NEED_UPDATES_LABEL
                    }
                  />
                </WhmTypography>
              ) : undefined
            }
            button={
              compressed ? undefined : (
                <WhmButton
                  variant={
                    entityInformationActionButton === ActionButton.MANAGE
                      ? "contained"
                      : "outlined"
                  }
                  size="large"
                  onClick={() =>
                    navigateToSection(
                      navigate,
                      editableProfileEnabled
                        ? "/profile/information"
                        : "/profile/verification/entity-information",
                      !!editableProfileEnabled,
                    )
                  }
                >
                  {entityInformationActionButton === ActionButton.MANAGE ? (
                    <Translate
                      translationKey={
                        translationKeys.ONBOARDING_CARD_MANAGE_BUTTON
                      }
                    />
                  ) : (
                    <Translate
                      translationKey={
                        translationKeys.ONBOARDING_CARD_REVIEW_BUTTON
                      }
                    />
                  )}
                </WhmButton>
              )
            }
          />
          <WhmDivider />
          <EntityOnboardingSection
            icon={EntityOnboardingIcon.documents}
            title={
              <Translate
                translationKey={
                  translationKeys.ONBOARDING_CARD_ENTITY_DOCUMENTS_TITLE
                }
              />
            }
            subtitle={
              <Translate
                replacements={
                  documentsMissingAmount
                    ? { quantity: documentsMissingAmount }
                    : {}
                }
                translationKey={
                  translationKeys.ONBOARDING_CARD_ENTITY_DOCUMENTS_SUBTITLE
                }
              />
            }
            statusMessage={
              kyb.documents.status === KybSectionStatuses.UPDATES_REQUIRED ? (
                <WhmTypography
                  variant="body2"
                  sx={{ textTransform: "uppercase" }}
                >
                  <WhmTypography
                    component="span"
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color:
                        documentsNeedingUpdatesCount === 0
                          ? "primary.main"
                          : "error.main",
                    }}
                  >
                    <Translate
                      translationKey={
                        translationKeys.ONBOARDING_CARD_ENTITY_DOCUMENTS_UPDATES_REQUIRED_COUNT
                      }
                      replacements={{ quantity: documentsNeedingUpdatesCount }}
                    />
                  </WhmTypography>{" "}
                  <Translate
                    translationKey={
                      translationKeys.ONBOARDING_CARD_UPDATES_REQUIRED_NEED_UPDATES_LABEL
                    }
                  />
                </WhmTypography>
              ) : undefined
            }
            button={
              compressed ? undefined : (
                <WhmButton
                  variant={
                    documentsActionButton === ActionButton.MANAGE
                      ? "contained"
                      : "outlined"
                  }
                  size="large"
                  onClick={() =>
                    navigateToSection(
                      navigate,
                      editableProfileEnabled
                        ? "/profile/documents"
                        : "/profile/verification/entity-documents",
                      !!editableProfileEnabled,
                    )
                  }
                >
                  {documentsActionButton === ActionButton.MANAGE ? (
                    <Translate
                      translationKey={
                        translationKeys.ONBOARDING_CARD_MANAGE_BUTTON
                      }
                    />
                  ) : (
                    <Translate
                      translationKey={
                        translationKeys.ONBOARDING_CARD_REVIEW_BUTTON
                      }
                    />
                  )}
                </WhmButton>
              )
            }
          />
          <WhmDivider />
          <EntityOnboardingSection
            sx={{ paddingBottom: "0px" }}
            icon={EntityOnboardingIcon.keyParties}
            title={
              <Translate
                translationKey={
                  translationKeys.ONBOARDING_CARD_KEY_PARTIES_TITLE
                }
              />
            }
            subtitle={
              <Translate
                translationKey={
                  translationKeys.ONBOARDING_CARD_KEY_PARTIES_SUBTITLE
                }
              />
            }
            statusMessage={
              // Additional info can only be resolved by email, never by
              // editing a key party, so once the member count hits 0 the
              // "X member(s) and" prefix is dropped and only "additional
              // info need updates" remains.
              kyb.keyParties.status === KybSectionStatuses.UPDATES_REQUIRED ? (
                <WhmTypography
                  variant="body2"
                  sx={{ textTransform: "uppercase" }}
                >
                  {(!hasKeyPartiesAdditionalInfo ||
                    unresolvedKeyPartiesCount > 0) && (
                    <>
                      <WhmTypography
                        component="span"
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color:
                            !hasKeyPartiesAdditionalInfo &&
                            unresolvedKeyPartiesCount === 0
                              ? "primary.main"
                              : "error.main",
                        }}
                      >
                        <Translate
                          translationKey={
                            translationKeys.ONBOARDING_CARD_KEY_PARTIES_UPDATES_REQUIRED_COUNT
                          }
                          replacements={{
                            quantity: unresolvedKeyPartiesCount,
                          }}
                        />
                      </WhmTypography>{" "}
                    </>
                  )}
                  {hasKeyPartiesAdditionalInfo && (
                    <>
                      {unresolvedKeyPartiesCount > 0 && (
                        <>
                          <Translate
                            translationKey={
                              translationKeys.ONBOARDING_CARD_KEY_PARTIES_UPDATES_REQUIRED_AND
                            }
                          />{" "}
                        </>
                      )}
                      <WhmTypography
                        component="span"
                        variant="body2"
                        sx={{ fontWeight: 700, color: "error.main" }}
                      >
                        <Translate
                          translationKey={
                            translationKeys.ONBOARDING_CARD_KEY_PARTIES_UPDATES_REQUIRED_ADDITIONAL_INFO_LABEL
                          }
                        />
                      </WhmTypography>{" "}
                    </>
                  )}
                  <Translate
                    translationKey={
                      translationKeys.ONBOARDING_CARD_UPDATES_REQUIRED_NEED_UPDATES_LABEL
                    }
                  />
                </WhmTypography>
              ) : undefined
            }
            button={
              compressed ? undefined : (
                <WhmButton
                  variant={
                    keyPartiesActionButton === ActionButton.MANAGE
                      ? "contained"
                      : "outlined"
                  }
                  size="large"
                  onClick={() =>
                    navigateToSection(
                      navigate,
                      editableProfileEnabled
                        ? "/profile/key-parties"
                        : kyb.keyParties.status === KybSectionStatuses.COMPLETED
                          ? "/profile/verification/key-parties/check"
                          : "/profile/verification/key-parties",
                      !!editableProfileEnabled,
                    )
                  }
                >
                  {keyPartiesActionButton === ActionButton.MANAGE ? (
                    <Translate
                      translationKey={
                        translationKeys.ONBOARDING_CARD_MANAGE_BUTTON
                      }
                    />
                  ) : (
                    <Translate
                      translationKey={
                        translationKeys.ONBOARDING_CARD_REVIEW_BUTTON
                      }
                    />
                  )}
                </WhmButton>
              )
            }
          />
          {(!compressed || isUpdatesRequiredStatus) && (
            <EntityOnboardingAdditionalInfoSection
              comment={additionalInfoComment}
            />
          )}
        </WhmStack>

        <EntityOnboardingSubmitConfirmationModal
          open={showConfirmationModal}
          hasAdditionalInfoComment={!!additionalInfoComment}
          onClose={() => {
            setShowConfirmationModal(false);
          }}
        />
      </WhmCardContent>
    </WhmCard>
  );
}
